import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowLeft, Briefcase, Users, UserPlus, Clock } from 'lucide-react';
import { cachedGet } from '../lib/cache';
import { Avatar } from './Avatar';
import { dateRange, stageMeta } from '../lib/ui';

const RECENT_KEY = 'm_recent_searches';
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
const pushRecent = (q) => {
  const cur = getRecent().filter((x) => x.toLowerCase() !== q.toLowerCase());
  const next = [q, ...cur].slice(0, 6);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
  return next;
};

const arr = (d) => (Array.isArray(d) ? d : (d?.items || d?.leads || []));

/**
 * Full-screen universal search overlay. 200ms debounce, instant grouped
 * results (Trips / Travellers / Leads), recent searches before typing.
 */
export const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ trips: [], clients: [], leads: [] });
  const [recent, setRecent] = useState(getRecent());
  const inputRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRecent(getRecent());
    setTimeout(() => inputRef.current?.focus(), 60);
    Promise.all([
      cachedGet('/api/trips').catch(() => []),
      cachedGet('/api/clients?limit=500').catch(() => []),
      cachedGet('/api/website-leads?limit=200').catch(() => []),
    ]).then(([t, c, l]) => setData({ trips: arr(t), clients: arr(c), leads: arr(l) }));
  }, [open]);

  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setQ(raw.trim().toLowerCase()), 200);
    return () => clearTimeout(debRef.current);
  }, [raw]);

  const results = useMemo(() => {
    if (!q) return { trips: [], clients: [], leads: [] };
    const m = (s) => String(s || '').toLowerCase().includes(q);
    return {
      trips: data.trips.filter((t) => m(t.guestName) || m(t.destination) || m(t.displayTripId) || m(t.phone)).slice(0, 6),
      clients: data.clients.filter((c) => m(c.fullName) || m(c.ucc) || m(c.displayUCC) || m(c.email) || m(c.phone)).slice(0, 6),
      leads: data.leads.filter((l) => m(l.name) || m(l.destination) || m(l.phone)).slice(0, 6),
    };
  }, [q, data]);

  if (!open) return null;
  const rTrips = results.trips;
  const rClients = results.clients;
  const rLeads = results.leads;
  const total = rTrips.length + rClients.length + rLeads.length;

  const go = (path) => { if (raw.trim()) { setRecent(pushRecent(raw.trim())); } onClose(); navigate(path); };

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="m-search-overlay">
      <div className="h-[52px] flex items-center gap-2 px-2 border-b border-[#E5E7EB] shrink-0">
        <button type="button" onClick={onClose} data-testid="m-search-close" aria-label="Close search" className="w-11 h-11 flex items-center justify-center text-[#111827]">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
          <input
            ref={inputRef}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Search trips, travellers, leads…"
            data-testid="m-search-input"
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E5E7EB] text-[15px] text-[#111827] focus:outline-none focus:border-[#0F6E56]"
          />
          {raw && (
            <button type="button" onClick={() => setRaw('')} aria-label="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-[#6B7280]">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {!q ? (
          <div>
            <p className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Recent</p>
            {recent.length === 0 ? (
              <p className="text-[13px] text-[#6B7280]">Search across your trips, travellers and leads.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {recent.map((r) => (
                  <button key={r} type="button" onClick={() => setRaw(r)} data-testid={`m-search-recent-${r}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E7EB] text-[13px] text-[#111827]">
                    <Clock className="h-3.5 w-3.5 text-[#6B7280]" />{r}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : total === 0 ? (
          <p className="text-[14px] text-[#6B7280] text-center mt-10" data-testid="m-search-empty">No matches for “{raw}”.</p>
        ) : (
          <div className="space-y-5">
            {rTrips.length > 0 && (
              <Group icon={Briefcase} label="Trips">
                {rTrips.map((t) => (
                  <Row key={t.id} onClick={() => go(`/m/trips/${t.id}`)} testid={`m-search-trip-${t.id}`}
                    avatar={<Avatar name={t.guestName || t.destination} size={36} />}
                    title={t.guestName && t.guestName !== '(Pending guest)' ? t.guestName : (t.destination || 'Trip')}
                    sub={[t.destination, dateRange(t.travelStartDate, t.travelEndDate)].filter(Boolean).join(' · ')}
                    tag={stageMeta(t).label}
                  />
                ))}
              </Group>
            )}
            {rClients.length > 0 && (
              <Group icon={Users} label="Travellers">
                {rClients.map((c) => (
                  <Row key={c.id || c.ucc} onClick={() => go(`/m/clients/${c.ucc}`)} testid={`m-search-client-${c.ucc}`}
                    avatar={<Avatar name={c.fullName} size={36} />}
                    title={c.fullName} sub={[c.displayUCC || c.ucc, c.phone].filter(Boolean).join(' · ')} />
                ))}
              </Group>
            )}
            {rLeads.length > 0 && (
              <Group icon={UserPlus} label="Leads">
                {rLeads.map((l) => (
                  <Row key={l.leadId || l.id} onClick={() => go(`/m/leads/${l.leadId || l.id}`)} testid={`m-search-lead-${l.leadId || l.id}`}
                    avatar={<Avatar name={l.name} size={36} />}
                    title={l.name} sub={[l.destination, l.source].filter(Boolean).join(' · ')} />
                ))}
              </Group>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Group = ({ icon: Icon, label, children }) => (
  <div>
    <p className="flex items-center gap-1.5 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
      <Icon className="h-3.5 w-3.5" />{label}
    </p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Row = ({ avatar, title, sub, tag, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-[#E5E7EB] text-left active:bg-[#F8F9FA]">
    {avatar}
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-medium text-[#111827] truncate">{title}</span>
      {sub && <span className="block text-[12px] text-[#6B7280] truncate">{sub}</span>}
    </span>
    {tag && <span className="shrink-0 text-[11px] font-semibold text-[#0F6E56]">{tag}</span>}
  </button>
);

export default SearchOverlay;
